/**
 * The console's navigation.
 *
 * The same pages the sidebar always had — overview, organizations and the
 * create screen, module requests with what is waiting, the catalog, plans and
 * service health — grouped by what they are for. It collapses to an icon rail
 * when a wide table needs the room (remembered per browser), and becomes a
 * drawer on a small screen.
 *
 * Pages you pinned (the star beside a page's title) follow the sections, so
 * the organization you are supporting this week is one click away.
 *
 * The active page is the LONGEST match rather than every prefix: on the create
 * screen, "Create organization" is lit and "Organizations" is not.
 */
import { Link, useLocation } from 'react-router-dom';
import { Icon } from '../components/Icon.jsx';
import { tenantAppUrl } from '../lib/tenantApp.js';
import { AppIcon } from './Brand.jsx';
import { activeItemIn } from './console.js';

const HEALTH_DOT = { degraded: 'warning', unavailable: 'danger' };

export function ContextNav({ app, collapsed, onToggleCollapsed, open, onNavigate, counts = {}, pinned = [] }) {
  const { pathname } = useLocation();
  const active = activeItemIn(app.items, pathname);
  const pinnedHere = pinned.find((entry) => entry.to === pathname);

  return (
    <aside
      className={`ctxnav ${collapsed ? 'ctxnav-collapsed' : ''} ${open ? 'ctxnav-open' : ''}`}
      aria-label={`${app.name} navigation`}
      data-app={app.key}
    >
      <Link
        to={app.home}
        className="ctxnav-head"
        onClick={onNavigate}
        title={collapsed ? app.name : undefined}
        aria-label={collapsed ? `${app.name} overview` : undefined}
      >
        <AppIcon app={app} size="md" />
        <span className="ctxnav-head-text">
          <span className="ctxnav-app-name">{app.name}</span>
          <span className="ctxnav-app-desc">{app.description}</span>
        </span>
      </Link>

      <nav className="ctxnav-nav">
        {app.sections.map((section, index) => (
          <div className="ctxnav-section" key={section.label ?? `section-${index}`}>
            {section.label && <div className="ctxnav-label">{section.label}</div>}
            {section.items.map((item) => {
              // A pinned page is lit in the pinned list instead, so one place
              // says "you are here".
              const isActive = item === active && !pinnedHere;
              const count = item.badge === 'moduleRequests' ? counts.moduleRequests ?? 0 : 0;
              const healthTone = item.badge === 'health' ? HEALTH_DOT[counts.health] : null;
              const status = count > 0 ? `, ${count} waiting for a decision` : healthTone ? `, ${counts.health}` : '';
              return (
                <div key={item.to} className="ctxnav-item">
                  <Link
                    to={item.to}
                    className={`ctxnav-link ${isActive ? 'ctxnav-link-active' : ''}`}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={onNavigate}
                    title={collapsed ? `${item.label}${status}` : undefined}
                    // Collapsed, the label is hidden — from screen readers too —
                    // so the link carries it as its name.
                    aria-label={collapsed ? `${item.label}${status}` : undefined}
                  >
                    <Icon name={item.icon} size={16} className="ctxnav-link-icon" />
                    <span className="ctxnav-link-label">{item.label}</span>
                    {count > 0 && (
                      <span className="ctxnav-count" title={`${count} waiting for a decision`}>
                        {count}
                      </span>
                    )}
                    {healthTone && (
                      <span
                        className={`ctxnav-status ctxnav-status-${healthTone}`}
                        title={counts.health === 'degraded' ? 'Some services are degraded' : 'Some services are not responding'}
                      />
                    )}
                  </Link>
                </div>
              );
            })}
          </div>
        ))}

        {pinned.length > 0 && (
          <div className="ctxnav-section">
            <div className="ctxnav-label">Pinned</div>
            {pinned.map((entry) => {
              const isActive = entry.to === pathname;
              return (
                <div key={entry.to} className="ctxnav-item">
                  <Link
                    to={entry.to}
                    className={`ctxnav-link ${isActive ? 'ctxnav-link-active' : ''}`}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={onNavigate}
                    title={collapsed ? entry.label : undefined}
                    aria-label={collapsed ? entry.label : undefined}
                  >
                    <Icon name="star" size={16} className="ctxnav-link-icon" />
                    <span className="ctxnav-link-label">{entry.label}</span>
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </nav>

      <div className="ctxnav-foot">
        <a
          href={tenantAppUrl()}
          className="ctxnav-link ctxnav-external"
          target="_blank"
          rel="noreferrer"
          title={collapsed ? 'Open the tenant app' : undefined}
          aria-label={collapsed ? 'Open the tenant app (opens in a new tab)' : undefined}
        >
          <Icon name="external-link" size={16} className="ctxnav-link-icon" />
          <span className="ctxnav-link-label">Open the tenant app</span>
        </a>
        <button
          type="button"
          className="ctxnav-collapse"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        >
          <Icon name={collapsed ? 'panel-left-open' : 'panel-left-close'} size={16} />
          <span className="ctxnav-link-label">Collapse</span>
        </button>
      </div>
    </aside>
  );
}
